import {
  type ComponentType,
  type SVGAttributes,
  useRef,
  useState,
} from "react";

import {
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  CodeBlockIcon,
  EmaileditorVideoIcon,
  Menu,
  MenuItem,
  MenuList,
  PromptInputMenuButton,
  Row,
  StylesIcon,
  TelescopeIcon,
  TemplatesIcon,
  Text,
  TextWithTooltip,
  Tooltip,
} from "@hightouch/htui";
import pluralize from "pluralize";

import { ToggleSwitchVisual } from "src/components/llm/components/composer-left-actions";
import {
  type EmailAssetKind,
  EmailAssetSelectionModal,
  type SelectedEmailAsset,
} from "src/components/llm/component-picker";
import {
  measureStyleMenuMaxHeight,
  type StyleOption,
  type StyleSelection,
  StyleMenuOptions,
} from "src/pages/ad-studio/components/ad-studio-style-select";
import { AdStudioTemplatePickerModal } from "src/pages/ad-studio/components/ad-studio-template-picker-modal";
import type { TemplateSelection } from "src/pages/ad-studio/components/ad-studio-template-selection";
import type { AdStudioTemplateCard } from "src/pages/ad-studio/templates/types";
import { AdStudioIcon } from "src/ui/icons/ad-studio";
import { LifecycleStudioIcon } from "src/ui/icons/lifecycle-studio";

type Icon = ComponentType<SVGAttributes<SVGElement>>;

/** One setting the caller holds: its current value and how to change it. */
export type Setting<T> = { value: T; onChange: (value: T) => void };

/** One channel's style, with the styles to choose from and the choice's label. */
export type StylePick = Setting<StyleSelection> & {
  options: StyleOption[] | undefined;
  label: string;
  /** Disables the row while the styles load, as the composer's "+" menu does. */
  isLoading?: boolean;
  /** Disables the row and says why, such as when there are no styles to pick. */
  disabledTooltip?: string;
};

/** The ad templates, with the brand's templates to choose from. */
export type AdTemplatesPick = Setting<TemplateSelection> & {
  options: AdStudioTemplateCard[] | undefined;
  /** Disables the row while the templates load. */
  isLoading?: boolean;
  /** Disables the row and says why, such as when there are no templates to pick. */
  disabledTooltip?: string;
};

type AdsPickerProps = {
  style: StylePick;
  templates: AdTemplatesPick;
  /** Omitted to hide the row. */
  creativeBoost?: Setting<boolean>;
  /** Omitted to hide the row. */
  videoEngine?: Setting<boolean>;
};

/** The style, templates, and toggles for the ads a canvas message starts. */
export function AdsPicker({
  style,
  templates,
  creativeBoost,
  videoEngine,
}: AdsPickerProps) {
  const [isPickingTemplates, setIsPickingTemplates] = useState(false);
  const { options } = templates;
  const ids = templates.value.ids;
  const toggles: ToggleRow[] = [
    ...(creativeBoost
      ? [
          {
            label: "Creative boost",
            icon: TelescopeIcon,
            setting: creativeBoost,
          },
        ]
      : []),
    ...(videoEngine
      ? [
          {
            label: "Beta video engine",
            icon: EmaileditorVideoIcon,
            setting: videoEngine,
          },
        ]
      : []),
  ];
  return (
    <>
      <CreativeMenu
        name="Ads"
        icon={AdStudioIcon}
        style={style}
        pickers={[
          {
            label: "Templates",
            icon: TemplatesIcon,
            value: selectionLabel(
              ids.length,
              options?.find(({ id }) => id === ids[0])?.name,
              "template",
            ),
            isLoading: templates.isLoading,
            disabledTooltip: templates.disabledTooltip,
            onOpen: () => setIsPickingTemplates(true),
          },
        ]}
        toggles={toggles}
      />
      {isPickingTemplates && options && (
        <AdStudioTemplatePickerModal
          templates={options}
          initialSelectedIds={ids}
          onClose={() => setIsPickingTemplates(false)}
          onConfirm={(next) => {
            templates.onChange({ ids: next });
            setIsPickingTemplates(false);
          }}
        />
      )}
    </>
  );
}

type EmailPickerProps = {
  /** The brands whose templates and components to browse; null for every brand. */
  brandProfileIds: readonly string[] | null;
  style: StylePick;
  templates: Setting<SelectedEmailAsset[]>;
  components: Setting<SelectedEmailAsset[]>;
};

/** The style, templates, and components for the emails a canvas message starts. Templates and components carry their names, and their modal browses its own library, so neither takes options. */
export function EmailPicker({
  brandProfileIds,
  style,
  templates,
  components,
}: EmailPickerProps) {
  const [picking, setPicking] = useState<EmailAssetKind | null>(null);
  const picked = picking === "template" ? templates : components;
  return (
    <>
      <CreativeMenu
        name="Email"
        icon={LifecycleStudioIcon}
        // Without a chosen style the email agent picks from all of them, so there is no "No style" to choose.
        allowNone={false}
        style={style}
        pickers={[
          {
            label: "Templates",
            icon: TemplatesIcon,
            value: selectionLabel(
              templates.value.length,
              templates.value[0]?.name,
              "template",
            ),
            onOpen: () => setPicking("template"),
          },
          {
            label: "Components",
            icon: CodeBlockIcon,
            value: selectionLabel(
              components.value.length,
              components.value[0]?.name,
              "component",
            ),
            onOpen: () => setPicking("component"),
          },
        ]}
      />
      {/* Mounted only while open: it reads the current selection once, as it opens. */}
      {picking && (
        <EmailAssetSelectionModal
          isOpen
          onClose={() => setPicking(null)}
          initialSelected={picked.value}
          onConfirm={(assets) => {
            picked.onChange(assets);
            setPicking(null);
          }}
          sources={[picking]}
          brandProfileIds={brandProfileIds}
        />
      )}
    </>
  );
}

/** A picker row's value: none, the one pick's name, or a count. */
function selectionLabel(
  count: number,
  firstName: string | undefined,
  noun: string,
) {
  if (count === 0) return null;
  if (count === 1 && firstName) return firstName;
  return pluralize(noun, count, true);
}

/** A row that opens one of the host's pickers. */
type PickerRow = {
  label: string;
  icon: Icon;
  value: string | null;
  isLoading?: boolean;
  /** Disables the row and says why once it has loaded. */
  disabledTooltip?: string;
  onOpen: () => void;
};

/** A row that flips a setting in place and keeps the menu open. */
type ToggleRow = {
  label: string;
  icon: Icon;
  setting: Setting<boolean>;
};

type CreativeMenuProps = {
  name: string;
  icon: Icon;
  allowNone?: boolean;
  style: StylePick;
  /** Rows below Style, in order. */
  pickers: PickerRow[];
  /** Rows below the pickers, in order. */
  toggles?: ToggleRow[];
};

/**
 * A pill whose menu lists one channel's settings, each with its current value.
 * Style drills into the style list in place, since the dock leaves no room for a menu beside it.
 */
function CreativeMenu({
  name,
  icon,
  allowNone,
  style,
  pickers,
  toggles = [],
}: CreativeMenuProps) {
  const { options: styles, label: styleLabel } = style;
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<"settings" | "style">("settings");
  const container = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);
  const firstItem = useRef<HTMLButtonElement>(null);

  // Focus follows to the new view's first row, so the keyboard keeps working after the swap.
  const showView = (next: "settings" | "style") => {
    setView(next);
    requestAnimationFrame(() => firstItem.current?.focus());
  };
  const close = () => {
    setIsOpen(false);
    setView("settings");
  };
  // The pill keeps its name and lights up when anything is set; the tooltip names what is set.
  const configured = [
    ...(style.value.kind !== "auto" ? [styleLabel] : []),
    ...pickers.flatMap(({ value }) => (value === null ? [] : [value])),
    ...toggles.flatMap(({ setting, label }) => (setting.value ? [label] : [])),
  ];
  const accessibleLabel =
    configured.length > 0
      ? `${name}: ${configured.join(", ")}`
      : `${name} settings`;

  return (
    // display="contents" keeps the wrapper out of the pill row's flex layout; it exists to measure the trigger.
    <Box display="contents" ref={container}>
      <Menu
        isOpen={isOpen}
        onOpen={() => {
          setMaxHeight(measureStyleMenuMaxHeight(container.current));
          setIsOpen(true);
        }}
        onClose={close}
        closeOnSelect={false}
      >
        <PromptInputMenuButton
          variant="compact"
          icon={icon}
          label={name}
          isActive={configured.length > 0}
          tooltip={accessibleLabel}
          ariaLabel={accessibleLabel}
        />
        <MenuList>
          {view === "settings" ? (
            <>
              <Box px={2} py={0.5}>
                <Text color="text.secondary" size="sm" fontWeight="medium">
                  {name}
                </Text>
              </Box>
              <Tooltip
                message={style.disabledTooltip}
                isDisabled={
                  style.isLoading || style.disabledTooltip === undefined
                }
                placement="right"
              >
                <MenuItem
                  ref={firstItem}
                  icon={StylesIcon}
                  isDisabled={
                    style.isLoading || style.disabledTooltip !== undefined
                  }
                  onClick={() => showView("style")}
                >
                  <SettingRow label="Style" value={styleLabel} />
                </MenuItem>
              </Tooltip>
              {pickers.map((picker) => (
                <Tooltip
                  key={picker.label}
                  message={picker.disabledTooltip}
                  isDisabled={
                    picker.isLoading || picker.disabledTooltip === undefined
                  }
                  placement="right"
                >
                  <MenuItem
                    icon={picker.icon}
                    isDisabled={
                      picker.isLoading || picker.disabledTooltip !== undefined
                    }
                    onClick={() => {
                      close();
                      picker.onOpen();
                    }}
                  >
                    <SettingRow
                      label={picker.label}
                      value={picker.value ?? "None"}
                    />
                  </MenuItem>
                </Tooltip>
              ))}
              {toggles.map(({ label, icon, setting }) => (
                <MenuItem
                  key={label}
                  icon={icon}
                  aria-label={`${label} ${setting.value ? "on" : "off"}`}
                  onClick={() => setting.onChange(!setting.value)}
                >
                  <Row
                    justify="space-between"
                    align="center"
                    gap={4}
                    width="100%"
                  >
                    <Text>{label}</Text>
                    <ToggleSwitchVisual isChecked={setting.value} />
                  </Row>
                </MenuItem>
              ))}
            </>
          ) : (
            <StyleMenuOptions
              styles={styles ?? []}
              value={style.value}
              onChange={(next) => {
                style.onChange(next);
                close();
              }}
              maxHeight={maxHeight}
              allowNone={allowNone}
              header={
                <MenuItem
                  ref={firstItem}
                  icon={ChevronLeftIcon}
                  onClick={() => showView("settings")}
                >
                  <Text color="text.secondary">Style</Text>
                </MenuItem>
              }
            />
          )}
        </MenuList>
      </Menu>
    </Box>
  );
}

function SettingRow({ label, value }: { label: string; value: string }) {
  return (
    <Row justify="space-between" align="center" gap={4} width="100%">
      <Text>{label}</Text>
      <Row
        align="center"
        gap={1}
        minWidth={0}
        maxWidth="40"
        color="text.secondary"
      >
        <TextWithTooltip color="text.secondary">{value}</TextWithTooltip>
        <Box as={ChevronRightIcon} boxSize={4} flexShrink={0} />
      </Row>
    </Row>
  );
}
